import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Plus, Trash2, Lock, LayoutTemplate } from 'lucide-react';
import { PageHeader, Field, Spinner, EmailPreview, CategoryBadge, CATEGORY_LABELS, Toggle, ConfirmDialog, Empty, PageLoader } from '../../components/ui';
import RichEditor from '../../components/RichEditor';
import api, { errMsg } from '../../services/api';

const BLANK = {
  name: '', category: 'GENERAL', defaultSubject: '', headerTitle: '', headerSub: '', showLogo: true,
  primaryColor: '', accentColor: '', defaultBody: '<p>Dear {{firstName}},</p><p></p>', footerText: '',
};

const pick = l => Object.fromEntries(Object.keys(BLANK).map(k => [k, l[k] ?? (typeof BLANK[k] === 'boolean' ? BLANK[k] : '')]));

function ColorInput({ value, onChange, fallback }) {
  return (
    <div className="flex items-center gap-2">
      <input type="color" value={value || fallback} onChange={e => onChange(e.target.value)} className="h-9 w-12 cursor-pointer rounded border border-gray-300" />
      <input className="input font-mono" value={value} placeholder={`${fallback} (group default)`} onChange={e => onChange(e.target.value)} />
    </div>
  );
}

export default function Layouts() {
  const [layouts, setLayouts] = useState(null);
  const [selected, setSelected] = useState(null); // layout id or 'new'
  const [form, setForm] = useState(BLANK);
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [group, setGroup] = useState(null);

  const load = () => api.get('/admin/layouts').then(r => { setLayouts(r.data); return r.data; });

  useEffect(() => {
    load().then(rows => { if (rows.length) { setSelected(rows[0].id); setForm(pick(rows[0])); } });
    api.get('/admin/settings').then(r => setGroup(r.data));
  }, []);

  // Live preview, debounced
  useEffect(() => {
    if (!selected) return;
    const t = setTimeout(() => {
      api.post('/admin/layouts/preview', { ...form, sampleAttachment: ['AGM'].includes(form.category) }).then(r => setPreview(r.data.html)).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [form, selected]);

  const current = layouts?.find(l => l.id === selected);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const choose = l => { setSelected(l.id); setForm(pick(l)); };
  const startNew = () => { setSelected('new'); setForm(BLANK); };

  const save = async () => {
    setBusy(true);
    try {
      if (selected === 'new') {
        const { data } = await api.post('/admin/layouts', form);
        await load();
        setSelected(data.id);
        toast.success('Layout created');
      } else {
        await api.put(`/admin/layouts/${selected}`, form);
        await load();
        toast.success('Layout saved');
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (!layouts) return <PageLoader />;

  return (
    <div>
      <PageHeader title="Email layouts" subtitle="Reusable, branded templates. Pick one when you write a notification."
        actions={<button onClick={startNew} className="btn-gold"><Plus size={16} />New layout</button>} />

      <div className="grid gap-6 xl:grid-cols-[240px_1fr_1fr]">
        <div className="card h-fit divide-y">
          {layouts.map(l => (
            <button key={l.id} onClick={() => choose(l)}
              className={`block w-full px-4 py-3 text-left transition-colors ${selected === l.id ? 'bg-navy-50 ring-2 ring-inset ring-gold-400' : 'hover:bg-gray-50'}`}>
              <p className="flex items-center gap-1.5 font-medium text-navy-900">{l.name}{l.isSystem && <Lock size={12} className="text-gray-400" />}</p>
              <div className="mt-1 flex items-center gap-2"><CategoryBadge category={l.category} /><span className="text-xs text-gray-400">used {l._count.notifications}×</span></div>
            </button>
          ))}
          {selected === 'new' && <div className="bg-navy-50 px-4 py-3 font-medium text-navy-900 ring-2 ring-inset ring-gold-400">New layout</div>}
          {!layouts.length && selected !== 'new' && <Empty icon={LayoutTemplate} title="No layouts" />}
        </div>

        {selected && (
          <div className="card space-y-4 p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Layout name" required><input className="input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. AGM announcement" /></Field>
              <Field label="Type" hint={current?.isSystem ? 'System layout — type is fixed' : 'Members can opt out of each type'}>
                <select className="input" value={form.category} disabled={current?.isSystem} onChange={e => set('category', e.target.value)}>
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Default subject" hint={form.category === 'BIRTHDAY' ? 'Used for every birthday email' : 'Pre-filled when you pick this layout'}>
              <input className="input" value={form.defaultSubject} onChange={e => set('defaultSubject', e.target.value)} />
            </Field>
            <div className="rounded-lg border p-4">
              <p className="mb-3 text-sm font-semibold text-navy-900">Header</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Title" hint="Blank = group name"><input className="input" value={form.headerTitle} onChange={e => set('headerTitle', e.target.value)} placeholder={group?.name} /></Field>
                <Field label="Subtitle"><input className="input" value={form.headerSub} onChange={e => set('headerSub', e.target.value)} /></Field>
                <Field label="Header colour"><ColorInput value={form.primaryColor} onChange={v => set('primaryColor', v)} fallback={group?.primaryColor || '#0E1529'} /></Field>
                <Field label="Accent colour"><ColorInput value={form.accentColor} onChange={v => set('accentColor', v)} fallback={group?.accentColor || '#E5A517'} /></Field>
              </div>
              <div className="mt-4"><Toggle checked={form.showLogo} onChange={v => set('showLogo', v)} label="Show group logo" /></div>
            </div>
            <Field label={form.category === 'BIRTHDAY' ? 'Birthday message' : 'Default message'}>
              <RichEditor value={form.defaultBody} onChange={v => set('defaultBody', v)} minHeight={160} />
            </Field>
            <Field label="Footer text" hint="Blank = group footer. An unsubscribe link is always added.">
              <textarea className="input" rows={3} value={form.footerText} onChange={e => set('footerText', e.target.value)} placeholder={group?.footerText} />
            </Field>
            <div className="flex flex-wrap gap-2 border-t pt-4">
              <button onClick={save} disabled={busy} className="btn-primary">{busy && <Spinner size={16} />}{selected === 'new' ? 'Create layout' : 'Save layout'}</button>
              {current && !current.isSystem && <button onClick={() => setConfirmDelete(true)} className="btn-ghost ml-auto text-red-600"><Trash2 size={16} />Delete</button>}
            </div>
          </div>
        )}

        {selected && (
          <div>
            <p className="label">Live preview</p>
            <EmailPreview html={preview} height={760} />
          </div>
        )}
      </div>

      {confirmDelete && (
        <ConfirmDialog title="Delete layout?" danger confirmLabel="Delete" message={`Delete "${current.name}"? Notifications already sent are not affected.`}
          onClose={() => setConfirmDelete(false)}
          onConfirm={async () => {
            await api.delete(`/admin/layouts/${current.id}`);
            const rows = await load();
            if (rows[0]) choose(rows[0]); else setSelected(null);
            toast.success('Layout deleted');
          }} />
      )}
    </div>
  );
}
